import React, { useState, useCallback, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { useAuditLogs } from "@/hooks/useAuditLogs"
import { useAuth } from "@/hooks/useAuth"
import { useUserMap } from "@/hooks/useUserMap"
import { exportAuditLogs } from "@/services/adminService"
import { Download, Filter, ChevronDown, ChevronRight, Loader2 } from "lucide-react"

const EVENT_TYPES = [
  "",
  "model_response",
  "tool_call",
  "guardrail_block",
  "error",
]
const EVENT_TYPE_LABELS: Record<string, string> = {
  "": "すべての種類",
  model_response: "モデル応答",
  tool_call: "ツール呼び出し",
  guardrail_block: "ガードレールブロック",
  error: "エラー",
}
const EVENT_TYPE_COLORS: Record<string, string> = {
  model_response: "bg-blue-100 text-blue-800",
  tool_call: "bg-green-100 text-green-800",
  guardrail_block: "bg-yellow-100 text-yellow-800",
  error: "bg-destructive/10 text-destructive",
}

export default function AuditLogViewer() {
  const { token } = useAuth()
  const { resolveEmail } = useUserMap()
  const [userId, setUserId] = useState("")
  const [eventType, setEventType] = useState("")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")
  const [expandedRow, setExpandedRow] = useState<number | null>(null)

  const fromTs = fromDate ? new Date(fromDate).getTime() : undefined
  const toTs = toDate ? new Date(toDate + "T23:59:59").getTime() : undefined

  const { events, loading, loadingMore, hasMore, error, refresh, loadMore } = useAuditLogs({
    userId: userId || undefined,
    eventType: eventType || undefined,
    from: fromTs,
    to: toTs,
    limit: 20,
  })

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!hasMore || loadingMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { threshold: 0.1 }
    )
    const sentinel = sentinelRef.current
    if (sentinel) observer.observe(sentinel)
    return () => {
      if (sentinel) observer.unobserve(sentinel)
    }
  }, [hasMore, loadingMore, loadMore])

  const handleExport = useCallback(
    async (format: "csv" | "json") => {
      if (!token) return
      try {
        const result = await exportAuditLogs(token, {
          format,
          userId: userId || undefined,
          from: fromTs,
          to: toTs,
        })
        const content =
          format === "csv"
            ? (result.data as string)
            : JSON.stringify(result.data, null, 2)
        const blob = new Blob([content], {
          type: format === "csv" ? "text/csv" : "application/json",
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `audit-logs.${format}`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        console.error("Export failed:", e)
      }
    },
    [token, userId, fromTs, toTs]
  )

  if (error) {
    return (
      <div className="text-center py-10 text-destructive">
        <p>Failed to load audit logs: {error}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">監査ログ</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("csv")}
          >
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport("json")}
          >
            <Download className="h-4 w-4 mr-1" />
            JSON
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            フィルター
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input
              placeholder="ユーザーID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            >
              {EVENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {EVENT_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              placeholder="開始日"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              placeholder="終了日"
            />
          </div>
          <div className="mt-3">
            <Button size="sm" onClick={refresh}>
              フィルターを適用
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            イベント{" "}
            {!loading && (
              <span className="text-sm font-normal text-muted-foreground">
                (表示中 {events.length} 件)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">
              イベントが見つかりません
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="w-8"></th>
                    <th className="text-left py-2 px-3">日時</th>
                    <th className="text-left py-2 px-3">種類</th>
                    <th className="text-left py-2 px-3">ユーザー</th>
                    <th className="text-left py-2 px-3">セッション</th>
                    <th className="text-left py-2 px-3">詳細</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event, i) => (
                    <React.Fragment key={event["timestamp#eventId"] || i}>
                      <tr
                        className="border-b cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          setExpandedRow(expandedRow === i ? null : i)
                        }
                      >
                        <td className="py-2 px-1">
                          {expandedRow === i ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          {new Date(event.timestamp).toLocaleString()}
                        </td>
                        <td className="py-2 px-3">
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              EVENT_TYPE_COLORS[event.eventType] ||
                              "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {event.eventType}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="text-xs">{resolveEmail(event.userId) || "-"}</div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {event.userId?.slice(0, 8)}...
                          </div>
                        </td>
                        <td className="py-2 px-3 font-mono text-xs">
                          {event.sessionId?.slice(0, 8)}...
                        </td>
                        <td className="py-2 px-3 truncate max-w-xs">
                          {event.toolName || event.outputSummary || "-"}
                        </td>
                      </tr>
                      {expandedRow === i && (
                        <tr key={`${i}-detail`} className="border-b bg-muted/30">
                          <td colSpan={6} className="py-3 px-6">
                            <pre className="text-xs overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(event, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {hasMore && (
                <div ref={sentinelRef} className="flex justify-center py-4">
                  {loadingMore && (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
